import { useEffect, useRef, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import axios, { CancelTokenSource } from 'axios'
import RadioGroup from '../../../../components/RadioGroup/RadioGroup'
import { Button, BUTTON_TYPES } from '../../../../components/Button'
import { Table } from '../../../../components/Table'
import { Input } from '../../../../components/Input'
import { UserArchivePopup } from '../../../../pages/CRUDUser/UserArchivePopup'
import { UserRolesFilterComponent } from '../../../../components/UserRolesFilterComponent/UserRolesFilterComponent'
import { ReactComponent as Search16 } from 'assets/svg/Search16.svg'
import { ReactComponent as Plus16 } from 'assets/svg/Plus16.svg'
import { ReactComponent as Edit16 } from 'assets/svg/Edit16.svg'
import { ReactComponent as Close16 } from 'assets/svg/Clos16.svg'
import useRouter from '../../../../hooks/useRouter'
import { setLoading } from '../../../../redux/store/common/slice'
import { useDebounce } from '../../../../hooks/hooks'
import { getUser } from '../../../../redux/store/user/getters'
import {
  fullFormatPhoneNumber,
  isTableHasParams
} from '../../../../helper/common'
import { getHealthSystemUsers } from './api'
import { ability, ACTIONS, Can, ROLES, SUBJECTS } from '../../../Permission'
import { routes } from '../../../../router'
import { BTN_TXT, PLACEHOLDERS, USER_STATUSES } from '../../../../constants'
import { DEFAULT_PAGE } from '../../../../components/Table/constants'
import { filterColumns } from './utils'
import { onStartChat } from '../../utils'
import { columns, USER_STATUSES_TABS } from './constants'
import { ROLES_LIST } from '../../MyOrganizationCommunity/HSDetails/ViewEditHS/Users/constants'
import { RadioChangeEvent } from 'antd'
import { FC } from 'react'
import { ColumnsType } from 'antd/es/table'
import {
  HealthSystemUser,
  HealthSystemUsersParams,
  HealthSystemUsersFilters,
  HealthSystemTableRequestData,
  HealthSystemUsersFiltersFunc,
  HealthSystemUsersProps
} from './types'
import './styles.scss'
import defineAbilityFor from '../../../Permission/ability'

export const HealthSystemUsers: FC<HealthSystemUsersProps> = ({ hsId }) => {
  const [searchValue, setSearchValue] = useState('')
  const [activeTab, setActiveTab] = useState<string | null>(null)
  const [tableData, setTableData] = useState<HealthSystemUser[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [sortParams, setSortParams] = useState<HealthSystemTableRequestData>()
  const [firstLoad, setFirstLoad] = useState(true)
  const [totalNumber, setTotalNumber] = useState(0)
  const [filters, setFilters] = useState<HealthSystemUsersFilters>({})
  const [tableColumns, setTableColumns] = useState<
    ColumnsType<HealthSystemUser>
  >([])
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
  const [selectedRows, setSelectedRows] = useState<HealthSystemUser[]>([])
  const [usersStatus, setUsersStatus] = useState(USER_STATUSES_TABS[0].value)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const tableDataTokenRef = useRef<CancelTokenSource>()
  const user = useSelector(getUser)
  const dispatch = useDispatch()
  const { push } = useRouter()

  const canEditUser = useMemo(() => {
    const role =
      activeTab === ROLES_LIST[0].value ? ROLES.VICE_PRESIDENT : activeTab
    return (
      ability.can(ACTIONS.CRUD, SUBJECTS.HEALTH_SYSTEM_USERS) &&
      (!role ||
        !defineAbilityFor(role, user).can(ACTIONS.EDIT, SUBJECTS.FORM_ITEM)) &&
      (user.role === ROLES.COMMUNITY_PRESIDENT || hsId === user.health_system)
    )
  }, [hsId, user, activeTab])

  const rolesList = useMemo(() => {
    const hideHospitalUser =
      user.role === ROLES.VENDOR_FINANCE_OFFICER ||
      user.role === ROLES.VENDOR_LEGAL ||
      user.role === ROLES.VENDOR_ANALYST

    if (!hideHospitalUser) {
      return ROLES_LIST
    }

    return ROLES_LIST.filter((role) => role.value !== ROLES.HOSPITAL_USER)
  }, [user.role])

  const debouncedSearch = useDebounce(searchValue, 500)

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(
      () => getTableData({ ...sortParams, ...filters }),
      firstLoad ? 0 : 500
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, activeTab, usersStatus])

  useEffect(() => {
    return () => {
      if (!!tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
    }
  }, [])

  const getTableData = ({
    sortField,
    sortOrder,
    filters,
    page
  }: HealthSystemTableRequestData) => {
    dispatch(setLoading(true))
    if (!!tableDataTokenRef.current) {
      tableDataTokenRef.current.cancel()
    }
    const params: HealthSystemUsersParams = { ...filters }
    if (searchValue) {
      params.search = searchValue
    }
    if (usersStatus === USER_STATUSES_TABS[1].value) {
      params.statuses = [usersStatus]
      setFilters((prev) => ({
        ...prev,
        statuses: []
      }))
    }
    if (sortField) {
      if (sortOrder) {
        params.ordering = `${sortOrder === 'descend' ? '-' : ''}${sortField}`
      }
      setSortParams({
        sortField,
        sortOrder
      })
    }
    const dataPage = page ? page : pageInfo
    params.limit = dataPage.pageSize
    params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
    tableDataTokenRef.current = axios.CancelToken.source()
    getHealthSystemUsers(hsId, activeTab, {
      cancelToken: tableDataTokenRef.current?.token,
      params
    })
      .then((resp) => {
        setTableColumns(
          filterColumns(
            activeTab || '',
            usersStatus,
            columns({
              selectedRole: activeTab,
              applyFilters,
              onStartChat,
              canEditUser,
              filters,
              user,
              hsId
            })
          )
        )
        if (!resp.data?.results) {
          return
        }
        setTableData(
          resp.data.results.map((i) => ({
            ...i,
            name: i.first_name,
            phone_number: fullFormatPhoneNumber(i.phone_number),
            key: i.uuid
          }))
        )
        setTotalNumber(resp.data.count)
        if (firstLoad) {
          setFirstLoad(false)
        }
      })
      .finally(() => dispatch(setLoading(false)))
  }

  const handleSelectRow = (selectedRowKeys, selectedRows) => {
    setSelectedRowKeys(selectedRowKeys)
    setSelectedRows(selectedRows)
  }

  const handleTableChange = (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      filters,
      page
    })
    setPageInfo(page)
  }

  const handleChangePageSize = (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    getTableData({
      ...sortParams,
      filters,
      page
    })
    setPageInfo(page)
  }

  const applyFilters: HealthSystemUsersFiltersFunc = (
    field,
    appliedFilters
  ) => {
    const newFilters = { ...filters, [field]: appliedFilters }
    setFilters(newFilters)
    getTableData({ ...sortParams, filters: newFilters })
  }

  const handleRedirect = () =>
    push(
      `${routes.createUserProfile}/${selectedRowKeys[0]}?fromCommunity=true`,
      {
        state: { selectedHs: hsId }
      }
    )

  const createUser = () =>
    push(`${routes.createUserProfile}/new?hsUser=${hsId}&fromCommunity=true`, {
      state: { selectedHs: hsId }
    })

  const onChangeRole = (tab: string) => {
    setSelectedRowKeys([])
    setSelectedRows([])
    setFilters({})
    setSortParams({})
    setSearchValue('')
    setActiveTab(tab)
    setTableColumns([])
  }

  const handleRadioGroup = ({ target: { value } }: RadioChangeEvent) =>
    setUsersStatus(value)

  return (
    <div className="hs-users-list column mt-16 gap-8">
      <div className="hs-users-list__header row align-center space-between">
        <div className="row align-center gap-8">
          <UserRolesFilterComponent roles={rolesList} onChange={onChangeRole} />
          <Input
            className="hs-users-list__search allow-clear"
            propsInput={{
              allowClear: { clearIcon: <Close16 /> },
              placeholder: PLACEHOLDERS.SEARCH_RFP_USERS,
              prefix: <Search16 />,
              value: searchValue,
              onChange: (e) => setSearchValue(e.target.value)
            }}
          />
        </div>
        <div className="hs-users-list__actions row align-center">
          <Can I={ACTIONS.CRUD} a={SUBJECTS.HEALTH_SYSTEM_USERS}>
            {!selectedRowKeys.length && hsId === user.health_system && (
              <Button
                type={BUTTON_TYPES.PRIMARY}
                icon={<Plus16 />}
                onClick={createUser}
                upperCase
              >
                {BTN_TXT.CREATE_USER}
              </Button>
            )}
          </Can>
          {selectedRowKeys.length === 1 && (
            <Can I={ACTIONS.EDIT} a={SUBJECTS.USER}>
              <Button
                type={BUTTON_TYPES.PRIMARY}
                onClick={handleRedirect}
                icon={<Edit16 />}
                upperCase
              >
                {BTN_TXT.EDIT}
              </Button>
            </Can>
          )}
          {selectedRowKeys.length > 0 &&
            selectedRows.find((i) => i?.status !== USER_STATUSES.ARCHIVED) && (
              <UserArchivePopup<HealthSystemUser>
                callback={() => {
                  setSelectedRowKeys([])
                  setSelectedRows([])
                  getTableData({ ...sortParams, ...filters })
                }}
                selected={selectedRows}
                isTable
              />
            )}
        </div>
        {/* TODO: this in not available to any other role - recheck this workflow if it needed */}
        <Can I={ACTIONS.CRUD} a={SUBJECTS.APPROVE_PENDING_USERS}>
          <RadioGroup.TimePeriod
            options={USER_STATUSES_TABS}
            defaultValue={USER_STATUSES_TABS[0].value}
            onChange={handleRadioGroup}
            value={usersStatus}
          />
        </Can>
      </div>
      <Table<HealthSystemUser>
        dataSource={!tableColumns.length ? [] : tableData}
        columns={tableColumns}
        emptyText="No users found"
        className="community-users-table"
        onChange={handleTableChange}
        onChangePage={handleChangePageSize}
        pageSize={pageInfo.pageSize}
        pagination={{
          pageSize: pageInfo.pageSize,
          current: pageInfo.pageNumber,
          total: totalNumber
        }}
        hasSearchOrFilters={isTableHasParams<HealthSystemUsersFilters>(
          debouncedSearch,
          filters
        )}
        createButton={
          canEditUser
            ? {
                buttonText: BTN_TXT.CREATE_USER,
                availability: true,
                action: createUser
              }
            : undefined
        }
        rowSelection={
          canEditUser
            ? {
                selectedRowKeys,
                onChange: handleSelectRow,
                getCheckboxProps: (record) => ({
                  disabled: record.uuid === user.uuid
                })
              }
            : undefined
        }
      />
    </div>
  )
}
